import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const getApiUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL;
  if (!url) {
    console.error('REACT_APP_BACKEND_URL is not set!');
    return '';
  }
  return url + '/api';
};

const API_URL = getApiUrl();

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

// Global 402 interceptor — redirect to /pricing when backend signals subscription required
let _subscriptionRedirectArmed = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error?.response?.status === 402 &&
      error?.response?.data?.detail?.code === 'subscription_required' &&
      !_subscriptionRedirectArmed &&
      typeof window !== 'undefined' &&
      !window.location.pathname.startsWith('/pricing') &&
      !window.location.pathname.startsWith('/auth') &&
      !window.location.pathname.startsWith('/admin')
    ) {
      _subscriptionRedirectArmed = true;
      // Defer to next tick so the failed promise still rejects to the caller
      setTimeout(() => {
        window.location.assign('/pricing?required=1');
      }, 50);
    }
    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasActiveSubscription = useCallback(() => {
    if (!user) return false;
    const sub = user.subscription;
    if (!sub) return false;
    return sub.status === 'active' || sub.status === 'trialing';
  }, [user]);

  // On mount, restore session from httpOnly cookie via /auth/me
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/me`);
        setUser(response.data);
      } catch (err) {
        // 401 here is the normal "no session" case — only log unexpected errors
        if (err?.response?.status && err.response.status !== 401) {
          console.warn('[auth] session restore failed:', err.response.status, err.message);
        }
      } finally {
        setLoading(false);
      }
    };
    restoreSession();
  }, []);

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { user: userData, access_token } = response.data;
      // Keep the token in memory as a Bearer fallback for the IMMEDIATELY following
      // request — prevents the cross-origin Set-Cookie race condition where the
      // browser hasn't committed the cookie yet by the time the next XHR fires.
      if (access_token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      }
      setUser(userData);
      setError(null);
      return userData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email, password, name, plan = 'market_leader', refCode = null) => {
    try {
      // Auto-pull ref code from URL or sessionStorage if not passed explicitly
      let ref_code = refCode;
      if (!ref_code && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        ref_code = params.get('ref') || sessionStorage.getItem('adwup_ref_code') || null;
      }
      const payload = { email, password, name, plan };
      if (ref_code) payload.ref_code = ref_code;
      const response = await axios.post(`${API_URL}/auth/register`, payload);
      const { user: userData, access_token } = response.data;
      // Use the freshly minted token as a Bearer fallback so the IMMEDIATELY-following
      // POST /api/billing/checkout works even if the Set-Cookie hasn't reached the
      // browser cookie jar yet (race condition with SameSite=none cross-origin cookies).
      if (access_token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      }
      setUser(userData);
      setError(null);
      return userData;
    } catch (error) {
      if (error.message === "Network Error") {
        error.message = "Unable to connect to our servers. Please check your connection and try again.";
      }
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (err) {
      // Always log out client-side regardless — but record the upstream failure for ops visibility
      console.warn('[auth] logout API call failed (clearing local session anyway):', err?.message);
    }
    // Clear the in-memory Bearer token as well
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, register, logout, hasActiveSubscription, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
